import { resizeImageBlob } from './imageResize';
import {
    clearProductFolder,
    dataUrlToBlob,
    fetchAsDataUrl,
    listProductImages,
    uploadProductImage,
} from './imageApi';

// Subida/borrado de las imágenes de un producto.
//
// ENI-167: antes esto hablaba directo con Firebase Storage desde el navegador, lo que obligaba a
// dejar el bucket abierto a `write` para cualquiera. Ahora va por el backend (`utils/imageApi`),
// que escribe con la service account. Las tres copias que había —AddProductPage, EditProductPage
// y este módulo— quedaron unificadas acá de paso: eran el mismo código y el comentario original
// ya marcaba este archivo como el destino natural.
//
// `images` es el mapa que emite ProductImagesPickerComponent:
//   { thumbnail: {path, position}, 1: {...}, 2: {...} }
// donde `path` es un data URL.

// La posición '0' es la opción "None" del selector del grid (GridImagesComponent): significa
// "esta imagen no va". Antes se subía igual, a `products/<code>/0`, un objeto que el storefront
// nunca pide (busca `thumbnail`, `1`, `2`, `3`) — o sea que "None" no quitaba nada, sólo dejaba
// basura. Ahora se filtra acá, que es lo que el selector dice que hace. Además el backend
// rechaza la posición 0, así que sin este filtro la subida fallaría.
function uploadables(images) {
    return Object.keys(images || {})
        .map((key) => images[key])
        .filter((image) => image != null && image !== '' && image.path != null && image.path !== '')
        .filter((image) => String(image.position) !== '0');
}

/**
 * Sube las imágenes a `folder` ('/products/123', '/accessories/9', '/used/7').
 * Devuelve una promesa con la cantidad subida. `onProgress(0..100)` es opcional.
 *
 * Sólo el thumbnail se redimensiona: las posiciones numéricas son la galería y se suben en su
 * tamaño original, que es la fuente de verdad. resizeImageBlob nunca agranda.
 *
 * Las subidas van de a una y en serie: cada una es un request al backend, y cuatro fotos de
 * varios MB en paralelo llenan la memoria del server sin necesidad.
 *
 * ⚠️ Una que falle NO corta a las siguientes. Importa porque el flujo de edición borra la carpeta
 * ANTES de llamar acá: si la primera imagen falla y se abandona, el producto queda con CERO
 * imágenes en vez de con las que sí se podían subir. Se intentan todas y recién al final se
 * lanza, con el detalle de cuáles quedaron afuera.
 */
/** Lado mayor del chip de la galería. La tira del detalle rinde 150px CSS, o sea 300 en retina. */
const CHIP_MAX_PX = 300;

/**
 * Sube el derivado chico de una foto de galería: `{carpeta}/{code}/chip{N}`.
 *
 * POR QUÉ. La tira de la ficha pintaba el ORIGINAL en un chip de 150px. Medido contra el bucket
 * de prod sobre las 1523 fotos de galería: promedian **168 KB** y la peor pesa 1,2 MB. En la
 * ficha eso eran **351 KB compitiendo con la imagen del LCP** — más que los dos scripts de gtag
 * juntos. El derivado de 300px pesa ~15 KB: un 97% menos.
 *
 * Sólo para posiciones numéricas **>= 2**. La 1 ya tiene su derivado —`thumbnail`— y además es
 * la foto que se muestra en grande, así que su chip reusa esa URL y sale de la caché.
 *
 * ⚠️ NO rompe la subida si falla. La foto original ya está en el bucket, que es lo que importa;
 * si falta el chip, el storefront cae al original y se ve igual, sólo que pesado. Cortar acá
 * dejaría al operador creyendo que la imagen no se subió.
 *
 * El precio de eso es que el fallo es INVISIBLE: nadie se entera de que una ficha quedó pesada.
 * Por eso se loguea con un prefijo buscable, y por eso existe
 * `backend/scripts/galeria-chips/4-verificar.js`, que audita la deriva entre el bucket y lo que
 * debería haber. Sin una de las dos cosas, esto se degrada solo y en silencio.
 */
function subirChip(folder, position, blob) {
    const n = Number(position);
    if (!Number.isInteger(n) || n < 2) {
        return Promise.resolve();
    }
    return resizeImageBlob(blob, CHIP_MAX_PX)
        .then((chip) => uploadProductImage(folder, `chip${n}`, chip))
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.warn(`[galeria-chips] no se pudo generar el derivado de ${folder}/${n}: `
                + `${error && error.message}. La foto se subió igual; la ficha queda pesada hasta `
                + 'que se corra backend/scripts/galeria-chips/2-backfill.js');
        });
}

export function uploadProductImages(folder, images, onProgress) {
    const lista = uploadables(images);
    if (lista.length === 0) {
        return Promise.resolve(0);
    }
    let subidas = 0;
    const fallidas = [];
    return lista.reduce((cadena, image) => cadena.then(() => (
        dataUrlToBlob(image.path)
            .then((blob) => (image.position === 'thumbnail' ? resizeImageBlob(blob) : blob))
            .then((blob) => uploadProductImage(folder, image.position, blob).then(() => subirChip(folder, image.position, blob)))
            .then(() => { subidas += 1; })
            .catch((error) => { fallidas.push(`${image.position} (${error.message})`); })
            .then(() => {
                if (typeof onProgress === 'function') {
                    onProgress(((subidas + fallidas.length) / lista.length) * 100);
                }
            })
    )), Promise.resolve()).then(() => {
        if (fallidas.length > 0) {
            throw new Error(`No se pudieron subir ${fallidas.length} de ${lista.length} imágenes: ${fallidas.join(', ')}`);
        }
        return lista.length;
    });
}

/**
 * Vacía la carpeta antes de reescribirla (flujo de edición).
 *
 * Es destructivo a propósito y por eso va SIEMPRE seguido de la subida: el picker carga las
 * imágenes existentes al abrir la pantalla y las reenvía, así que lo que se borra se vuelve a
 * escribir. Si alguna vez se llama sin subir después, el producto queda sin imágenes.
 */
/**
 * Carga las imágenes que ya están en el bucket para `folder` y las entrega como el mapa que
 * consumen el picker y los forms: `{ thumbnail: {path, position}, 1: {...} }`, donde `path` es un
 * data URL.
 *
 * `onImage(position, dataUrl)` se llama a medida que cada una llega, para que la pantalla pueda
 * mostrarlas de a una en vez de esperar a todas.
 *
 * Resuelve con el mapa completo. Que la promesa SE ASIENTE (resuelva o falle) es además la señal
 * de que ya no queda nada en vuelo, y es lo que las pantallas usan para habilitar el guardado:
 * hasta ese momento el mapa está incompleto y guardar borra del bucket lo que todavía no llegó
 * (ver el aviso de abajo y la guarda de EditProductPage).
 *
 * Estaba copiado en FormBoxGameComponent, FormAccessoryComponent y ProductImagesPickerComponent
 * —los dos primeros byte a byte, el tercero con una variante que reintroducía la carrera de
 * `setState`—. Vive acá por lo mismo que la subida.
 */
export function loadProductImages(folder, onImage) {
    return listProductImages(folder).then((lista) => {
        const mapa = {};
        const fallidas = [];
        return Promise.all((lista || []).map((item) => (
            fetchAsDataUrl(item.contentPath)
                .then((dataUrl) => {
                    mapa[item.position] = { path: dataUrl, position: item.position };
                    if (typeof onImage === 'function') onImage(item.position, dataUrl);
                })
                // Una imagen que no baja no puede tumbar a las otras: se sigue con las que sí,
                // pero NO se traga el hecho — ver el aviso de abajo.
                .catch(() => { fallidas.push(item.position); })
        ))).then(() => {
            // ⚠️ Esto NO es cosmético. El guardado de la edición borra la carpeta entera y
            // reescribe sólo lo que está en este mapa, así que cada imagen que no se pudo bajar
            // se perdería del bucket para siempre — y en silencio, porque la pantalla se ve
            // normal, sólo que con una foto menos. Avisar es lo mínimo: el admin puede cancelar
            // y volver a entrar en vez de guardar sobre una carga incompleta.
            if (fallidas.length > 0) {
                alert(
                    `No se pudieron cargar ${fallidas.length} imagen(es) que este producto ya tiene `
                    + `(posiciones: ${fallidas.join(', ')}).\n\n`
                    + 'Si guardás ahora, esas imágenes SE PIERDEN. Salí sin guardar y volvé a entrar.',
                );
            }
            return mapa;
        });
    });
}

export function clearProductImages(folder) {
    // Una carpeta inexistente no es un error: es un producto sin imágenes. El backend responde
    // ok igual, pero si la llamada falla por otro motivo tampoco se corta la edición — el
    // producto ya se guardó y lo peor que pasa es que queden imágenes viejas que la subida
    // siguiente pisa.
    return clearProductFolder(folder).catch(() => {});
}
